import { Flags } from '@fullstory/client-api';
import { Behavior, Constraints, Device, SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import {
  ComputeElementStatsRequest,
  ComputeElementStatsResponse,
  ComputeScrollMapsStatsResponse,
  QueryCacheOptions,
} from '@fullstory/proto/fs/services/alexa';
import {
  HeatmapsPublic,
  ArchiveHeatmapRequest,
  CreateUnnamedHeatmapRequest,
  GetAllHeatmapsRequest,
  GetUnnamedHeatmapRequest,
  MarkHeatmapViewedRequest,
  SetHeatmapRequest,
  StarHeatmapRequest,
} from '@fullstory/proto/fs/services/dx-objects';
import { CanAccessRequest } from '@fullstory/proto/fs/shared/accesscontrol';
import { int64, Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { createGrpcAction, RequestActionType } from '@fullstory/redux-util';
import { defaultCacheKey } from '@fullstory/requests';
import { createAppCacheActions } from '@fullstory/redux-util/utils/appCache';
import { ActionType } from '@fullstory/redux-util/utils/types';
import { ComputeMetricResponseWithId, LegacyMetricsRequestBody } from '../../metrics';
import { SearchCacheKey } from '../../searchMeta';
import { HeatmapsRequestTypes } from '../types';

const DX_OBJECTS_GET_ALL_HEATMAPS_ENDPOINT = HeatmapsPublic.GetAllHeatmapsEndpoint;

export interface HeatmapsDataType {
  pageviewMetrics: ComputeMetricResponseWithId | null | undefined;
  userMetrics: ComputeMetricResponseWithId | null | undefined;
  timeOnPageMetrics: ComputeMetricResponseWithId | null | undefined;
  medianScrollDepthMetrics: ComputeMetricResponseWithId | null | undefined;
  clickTypeStats: ComputeElementStatsResponse | null | undefined;
  allClickStats: ComputeElementStatsResponse | null | undefined;
  scrollResults: ComputeScrollMapsStatsResponse | null | undefined;
  computedAt: timestamp | null;
}

export type DirectFocus =
  | {
      type: 'selector';
      selector: string;
    }
  | {
      type: 'element';
      id: string;
    };

export interface HeatmapsPayload {
  behavior: Behavior;
  cacheOptions: QueryCacheOptions;
  clickType: ComputeElementStatsRequest.StatsType;
  constraints: Constraints;
  deviceType: Device;
  flags: Flags;
  orgId: string;
  scopedSegmentDefinition: SegmentDefinition;
  searchCacheKey: SearchCacheKey;
  pageviewMetric: LegacyMetricsRequestBody;
  timeOnPageMetric: LegacyMetricsRequestBody;
  userMetricsBody?: LegacyMetricsRequestBody | null;
  medianScrollDepthMetricsBody: LegacyMetricsRequestBody;
  allClickMetricsBody?: LegacyMetricsRequestBody | null;
  altClickTypeClickMetricsBody?: LegacyMetricsRequestBody | null;
  pageDefId: int64 | undefined;
  specId: string | undefined;
  // Whether to modify scroll map query to work with page vars
  joinOnPageViewCriteriaForScrollMaps: boolean;
  focusedOnlyOn: DirectFocus | undefined;
}

export type HeatmapsActions =
  | RequestActionType<typeof HeatmapsRequestActions>
  | ActionType<typeof HeatmapsActions>;

export const HeatmapsRequestActions = {
  archiveHeatmap: (body: ArchiveHeatmapRequest) =>
    createGrpcAction(
      {
        body,
        force: true,
        grpcFunction: HeatmapsPublic.ArchiveHeatmap,
        invalidateCacheKey: cacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
        type: HeatmapsRequestTypes.ArchiveHeatmapRequest,
      },
      HeatmapsRequestTypes.ArchiveHeatmapSuccess,
      HeatmapsRequestTypes.ArchiveHeatmapFailure,
    ),
  createUnnamedHeatmap: (body: CreateUnnamedHeatmapRequest) =>
    createGrpcAction(
      {
        body,
        grpcFunction: HeatmapsPublic.CreateUnnamedHeatmap,
        readWrite: 'write',
        type: HeatmapsRequestTypes.CreateUnnamedHeatmapRequest,
      },
      HeatmapsRequestTypes.CreateUnnamedHeatmapSuccess,
      HeatmapsRequestTypes.CreateUnnamedHeatmapFailure,
    ),
  getAllHeatmaps: (body: GetAllHeatmapsRequest) =>
    createGrpcAction(
      {
        body,
        grpcFunction: HeatmapsPublic.GetAllHeatmaps,
        readWrite: 'write',
        type: HeatmapsRequestTypes.GetAllHeatmapsRequest,
      },
      HeatmapsRequestTypes.GetAllHeatmapsSuccess,
      HeatmapsRequestTypes.GetAllHeatmapsFailure,
    ),
  markViewed: (body: MarkHeatmapViewedRequest) =>
    createGrpcAction(
      {
        body,
        force: true,
        grpcFunction: HeatmapsPublic.MarkHeatmapViewed,
        readWrite: 'write',
        type: HeatmapsRequestTypes.MarkViewedRequest,
      },
      HeatmapsRequestTypes.MarkViewedSuccess,
      HeatmapsRequestTypes.MarkViewedFailure,
      {
        modify: () => ({ lastViewed: Timestamp.now() }),
      },
    ),
  setHeatmap: (body: SetHeatmapRequest) =>
    createGrpcAction(
      {
        body,
        force: true,
        grpcFunction: HeatmapsPublic.SetHeatmap,
        invalidateCacheKey: cacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
        type: HeatmapsRequestTypes.SetHeatmapsRequest,
      },
      HeatmapsRequestTypes.SetHeatmapsSuccess,
      HeatmapsRequestTypes.SetHeatmapsFailure,
    ),
  starHeatmap: (body: StarHeatmapRequest) =>
    createGrpcAction(
      {
        body,
        force: true,
        grpcFunction: HeatmapsPublic.StarHeatmap,
        invalidateCacheKey: cacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
        type: HeatmapsRequestTypes.StarHeatmapRequest,
      },
      HeatmapsRequestTypes.StarHeatmapSuccess,
      HeatmapsRequestTypes.StarHeatmapFailure,
    ),
  getUnnamedHeatmap: (body: GetUnnamedHeatmapRequest) =>
    createGrpcAction(
      {
        body,
        grpcFunction: HeatmapsPublic.GetUnnamedHeatmap,
        readWrite: 'read',
        type: HeatmapsRequestTypes.GetUnnamedHeatmapRequest,
      },
      HeatmapsRequestTypes.GetUnnamedHeatmapSuccess,
      HeatmapsRequestTypes.GetUnnamedHeatmapFailure,
    ),
  getAccess: (body: CanAccessRequest) =>
    createGrpcAction(
      {
        type: HeatmapsRequestTypes.GetHeatmapAccessRequest,
        grpcFunction: HeatmapsPublic.GetHeatmapsAccess,
        body,
        readWrite: 'read',
      },
      HeatmapsRequestTypes.GetHeatmapAccessSuccess,
      HeatmapsRequestTypes.GetHeatmapAccessFailure,
    ),
};

export const HeatmapsActions = createAppCacheActions(
  HeatmapsRequestTypes.HeatmapsAppCacheIsLoading,
  HeatmapsRequestTypes.HeatmapsAppCacheLoadFailed,
  HeatmapsRequestTypes.HeatmapsAppCacheRefetching,
  HeatmapsRequestTypes.HeatmapsAppCacheHasData,
  HeatmapsRequestTypes.HeatmapsAppCacheFresh,
  HeatmapsRequestTypes.HeatmapsAppCacheSwap,
  HeatmapsRequestTypes.HeatmapsAppCacheClear,
)<HeatmapsDataType>();

const makeGetAllHeatmapsRequest = (
  orgId: string,
  includeArchived: boolean,
): GetAllHeatmapsRequest => ({ orgId, includeArchived });

const cacheKeysToInvalidate = (orgId: string) => {
  const endpoint = DX_OBJECTS_GET_ALL_HEATMAPS_ENDPOINT;

  const getAllHeatmapsRequest = makeGetAllHeatmapsRequest(orgId, true);
  const getActiveHeatmapsRequest = makeGetAllHeatmapsRequest(orgId, false);
  return [
    defaultCacheKey(endpoint, getAllHeatmapsRequest),
    defaultCacheKey(endpoint, getActiveHeatmapsRequest),
  ];
};
